import React, { useState, useEffect } from 'react';
import { Header } from '../components/layout/Header';
import { eventsAPI, visitorsAPI } from '../lib/api';
import { MapPin, Users, TrendingUp, RefreshCw, BarChart2 } from 'lucide-react';
import { toast } from 'sonner';

const UF_NAMES = {
  AC: 'Acre', AL: 'Alagoas', AP: 'Amapá', AM: 'Amazonas', BA: 'Bahia',
  CE: 'Ceará', DF: 'Distrito Federal', ES: 'Espírito Santo', GO: 'Goiás',
  MA: 'Maranhão', MT: 'Mato Grosso', MS: 'Mato Grosso do Sul', MG: 'Minas Gerais',
  PA: 'Pará', PB: 'Paraíba', PR: 'Paraná', PE: 'Pernambuco', PI: 'Piauí',
  RJ: 'Rio de Janeiro', RN: 'Rio Grande do Norte', RS: 'Rio Grande do Sul',
  RO: 'Rondônia', RR: 'Roraima', SC: 'Santa Catarina', SP: 'São Paulo',
  SE: 'Sergipe', TO: 'Tocantins',
};

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-5 flex items-center gap-4">
      <div className="p-3 rounded-xl" style={{ background: '#FDF6F0' }}>
        <Icon className="w-5 h-5" style={{ color: color || '#A0462D' }} />
      </div>
      <div>
        <p className="text-sm" style={{ color: '#A0462D' }}>{label}</p>
        <p className="text-2xl font-bold" style={{ color: '#2D0C10' }}>{value}</p>
      </div>
    </div>
  );
}

export default function Heatmap() {
  const [stateData, setStateData] = useState([]);
  const [cityData, setCityData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('states');

  const load = async () => {
    setLoading(true);
    try {
      const res = await eventsAPI.heatmap();
      const locations = res.data?.locations || [];

      const byState = {};
      const byCityMap = {};
      locations.forEach(({ city, state, count }) => {
        if (state) byState[state] = (byState[state] || 0) + count;
        if (city) {
          const key = `${city}|${state}`;
          byCityMap[key] = { city, state, count: (byCityMap[key]?.count || 0) + count };
        }
      });

      setStateData(
        Object.entries(byState)
          .map(([uf, count]) => ({ uf, name: UF_NAMES[uf] || uf, count }))
          .sort((a, b) => b.count - a.count)
      );
      setCityData(
        Object.values(byCityMap).sort((a, b) => b.count - a.count).slice(0, 30)
      );
    } catch {
      toast.error('Erro ao carregar dados de alcance');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const total = stateData.reduce((s, r) => s + r.count, 0);
  const maxCount = stateData[0]?.count || 1;

  return (
    <div>
      <Header
        title="Alcance por Região"
        subtitle="Distribuição geográfica de inscrições em eventos"
        actions={
          <button onClick={load} className="p-2 border border-stone-300 rounded-lg text-stone-500 hover:text-stone-700 transition-colors">
            <RefreshCw className="w-4 h-4" />
          </button>
        }
      />
      <div className="p-6 space-y-6">

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard icon={Users} label="Total de registros" value={total.toLocaleString('pt-BR')} />
          <StatCard icon={MapPin} label="Estados alcançados" value={stateData.length} />
          <StatCard icon={TrendingUp} label="Principal estado" value={stateData[0]?.name || '—'} />
        </div>

        <div className="flex gap-1 border-b border-stone-200">
          {[{ key: 'states', label: 'Por Estado' }, { key: 'cities', label: 'Por Cidade' }].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.key ? 'border-[#A0462D] text-[#A0462D]' : 'border-transparent text-stone-500 hover:text-stone-700'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-6 h-6 animate-spin" style={{ color: '#A0462D' }} />
          </div>
        ) : tab === 'states' ? (
          stateData.length === 0 ? (
            <div className="text-center py-20 text-stone-400">
              <BarChart2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nenhum dado disponível ainda.</p>
              <p className="text-xs mt-1">Os dados aparecem conforme inscrições de eventos são registradas.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {stateData.map(({ uf, name, count }, i) => {
                const pct = Math.round((count / maxCount) * 100);
                const totalPct = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
                const barColor = i === 0 ? '#A0462D' : i < 3 ? '#C4724F' : '#E8A87C';
                return (
                  <div key={uf} className="flex items-center gap-4">
                    <span className="w-5 text-xs text-stone-400 text-right font-mono">{i + 1}</span>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ background: '#FDF6F0', color: '#A0462D' }}>{uf}</div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium" style={{ color: '#2D0C10' }}>{name}</span>
                        <span className="text-sm text-stone-500">
                          {count.toLocaleString('pt-BR')}
                          <span className="text-xs text-stone-400 ml-1">({totalPct}%)</span>
                        </span>
                      </div>
                      <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${pct}%`, background: barColor }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          cityData.length === 0 ? (
            <div className="text-center py-20 text-stone-400">
              <MapPin className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nenhum dado de cidade disponível ainda.</p>
            </div>
          ) : (
            <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100 bg-stone-50">
                    <th className="text-left px-4 py-3 text-xs text-stone-500 font-medium">#</th>
                    <th className="text-left px-4 py-3 text-xs text-stone-500 font-medium">Cidade</th>
                    <th className="text-left px-4 py-3 text-xs text-stone-500 font-medium">Estado</th>
                    <th className="text-right px-4 py-3 text-xs text-stone-500 font-medium">Registros</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {cityData.map(({ city, state, count }, i) => (
                    <tr key={i} className="hover:bg-stone-50 transition-colors">
                      <td className="px-4 py-3 text-xs text-stone-400">{i + 1}</td>
                      <td className="px-4 py-3 font-medium" style={{ color: '#2D0C10' }}>{city}</td>
                      <td className="px-4 py-3 text-stone-500">{UF_NAMES[state] || state || '—'}</td>
                      <td className="px-4 py-3 text-right font-bold" style={{ color: '#A0462D' }}>
                        {count.toLocaleString('pt-BR')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </div>
  );
}
